const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://vs-back.thundergits.com/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('admin_token');

    // Check if the body is FormData
    const isFormData = options.body instanceof FormData;

    const config = {
      headers: {
        // Don't set Content-Type for FormData, let the browser set it with the correct boundary
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // Only stringify if it's not FormData and is an object
    if (config.body && typeof config.body === 'object' && !isFormData) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Check if the request was aborted
      if (response.status === 0) {
        throw new Error('Request was cancelled');
      }

      const data = await response.json();

      if (!response.ok) {
        // Create a detailed error object that includes validation details
        const error = new Error(data.message || `HTTP error! status: ${response.status}`);
        error.details = data.details;
        error.validationErrors = data.validationErrors;
        error.status = response.status;
        error.response = data;
        throw error;
      }

      return data;
    } catch (error) {
      // Don't log errors for aborted requests
      if (error.name !== 'AbortError' && error.message !== 'Request was cancelled') {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  // Auth endpoints
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: userData,
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: profileData,
    });
  }

  async changePassword(passwordData) {
    return this.request('/auth/change-password', {
      method: 'PUT',
      body: passwordData,
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword },
    });
  }

  // Users endpoints
  async getAllUsers(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/users${queryString}`);
  }

  async getUserById(userId) {
    return this.request(`/users/${userId}`);
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: userData,
    });
  }

  async updateUserStatus(userId, status) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: { status },
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getUserStats() {
    return this.request('/users/stats');
  }

  // Dedicated Yajman API endpoints
  // Expected response format for getAllYajmans:
  // {
  //   data: [
  //     {
  //       id: string,
  //       name: string,
  //       email: string,
  //       phone?: string,
  //       city?: string,
  //       status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED',
  //       isPremium: boolean,
  //       totalBookings: number,
  //       lastLoginAt?: string (ISO date),
  //       createdAt: string (ISO date)
  //     }
  //   ],
  //   pagination: {
  //     page: number,
  //     limit: number,
  //     total: number,
  //     totalPages: number,
  //     hasNext: boolean,
  //     hasPrev: boolean
  //   }
  // }
  async getAllYajmans(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/admin/yajmans${queryString}`);
  }

  async getYajmanById(yajmanId) {
    return this.request(`/admin/yajmans/${yajmanId}`);
  }

  // Expected response format for getYajmanStats:
  // {
  //   data: {
  //     totalYajmans: number,
  //     activeYajmans: number,
  //     inactiveYajmans: number,
  //     premiumYajmans: number,
  //     newYajmansThisMonth: number,
  //     yajmansByCity: { [city: string]: number },
  //     yajmansByJoinDate: { [date: string]: number }
  //   }
  // }
  async getYajmanStats() {
    return this.request('/admin/yajmans/stats');
  }

  async updateYajmanStatus(yajmanId, status) {
    return this.request(`/admin/yajmans/${yajmanId}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  async deleteYajman(yajmanId) {
    return this.request(`/admin/yajmans/${yajmanId}`, {
      method: 'DELETE',
    });
  }

  async getYajmanBookings(yajmanId, params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/admin/yajmans/${yajmanId}/bookings${queryString}`);
  }

  // Legacy methods - kept for backward compatibility
  async getYajmanUsers(params) {
    console.warn('getYajmanUsers is deprecated, use getAllYajmans instead');
    return this.getAllYajmans(params);
  }

  async assignUserRole(userId, roleId) {
    return this.request(`/users/${userId}/roles/${roleId}`, {
      method: 'POST',
    });
  }

  async removeUserRole(userId, roleId) {
    return this.request(`/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async getUserPermissions(userId) {
    return this.request(`/users/${userId}/permissions`);
  }

  // Pandits endpoints
  async getAllPandits(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/pandits${queryString}`);
  }

  async getPanditById(panditId) {
    return this.request(`/pandits/getPanditById/${panditId}`);
  }

  // Admin-specific pandit details endpoint with comprehensive data
  // Expected response format:
  // {
  //   success: true,
  //   statusCode: 200,
  //   message: "Pandit fetched successfully",
  //   data: {
  //     id: string,
  //     email: string,
  //     phone: string,
  //     status: string,
  //     role: string,
  //     isVerified: boolean,
  //     featuredPandit: boolean,
  //     createdAt: string,
  //     updatedAt: string,
  //     documents: array,
  //     bookings: array of booking objects,
  //     profile: {
  //       name: string,
  //       city: string,
  //       state?: string,
  //       country?: string,
  //       bio?: string,
  //       languages?: array,
  //       experience?: number,
  //       ratingAvg: number,
  //       ratingCount: number,
  //       image?: string
  //     },
  //     expertise: array of expertise objects with serviceDetails,
  //     blockedSlots: array of time slots
  //   },
  //   timestamp: string
  // }
  async getPanditDetailsById(panditId) {
    return this.request(`/admin/getPanditDetailsById/${panditId}`);
  }

  async getFeaturedPandits() {
    return this.request('/pandits/featured');
  }

  async getUnverifiedPandits(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/pandits/unverified${queryString}`);
  }

  async updatePandit(panditId, panditData) {
    return this.request(`/pandits/updatePandit/${panditId}`, {
      method: 'PUT',
      body: panditData,
    });
  }

  async verifyPandit(panditId) {
    return this.request(`/pandits/verify/${panditId}`, {
      method: 'PUT',
    });
  }

  async registerPandit(panditData) {
    return this.request('/pandits/register-pandit', {
      method: 'POST',
      body: panditData,
    });
  }

  async updatePanditProfile(profileData) {
    return this.request('/pandits/updatePanditProfile', {
      method: 'PUT',
      body: profileData,
    });
  }

  async getPanditBookings() {
    return this.request('/pandits/pandit-bookings');
  }

  // TODO: Backend endpoint not yet implemented - using mock data in PanditDetails component
  async getPanditBookingsById(panditId, params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/pandits/${panditId}/bookings${queryString}`);
  }

  async acceptBooking(bookingId) {
    return this.request(`/pandits/accept/${bookingId}`, {
      method: 'PUT',
    });
  }

  async rejectBooking(bookingId) {
    return this.request(`/pandits/reject/${bookingId}`, {
      method: 'PUT',
    });
  }

  async getPanditServices() {
    return this.request('/pandits/pandit/services');
  }

  async getPanditStats() {
    return this.request('/pandits/stats');
  }

  /**
   * Create a new pandit by admin
   * @param {Object} panditData - Complete pandit data including user and profile fields
   * @returns {Promise} API response with created pandit data
   */
  async createPanditByAdmin(panditData) {
    return this.request('/pandits/admin/create', {
      method: 'POST',
      body: panditData,
    });
  }

  /**
   * Get documents for a specific pandit (admin only)
   * @param {String} panditId - Pandit ID
   * @returns {Promise} API response with pandit documents
   */
  async getPanditDocuments(panditId) {
    return this.request(`/admin/pandits/${panditId}/documents`);
  }

  // Bulk operations for pandits
  async bulkVerifyPandits(panditIds) {
    return this.request('/pandits/bulk-verify', {
      method: 'PUT',
      body: { panditIds },
    });
  }

  async bulkRejectPandits(panditIds, reason) {
    return this.request('/pandits/bulk-reject', {
      method: 'PUT',
      body: { panditIds, reason },
    });
  }

  async requestMoreInfo(panditId, message) {
    return this.request(`/pandits/${panditId}/request-info`, {
      method: 'POST',
      body: { message },
    });
  }

  async rejectPanditApplication(panditId, reason) {
    return this.request(`/pandits/${panditId}/reject`, {
      method: 'PUT',
      body: { reason },
    });
  }

  /**
   * Reapply pandit profile after rejection (for pandits to submit updated profile)
   * @param {Object} updatedData - Updated profile and expertise data
   * @returns {Promise} API response
   */
  async reapplyPanditProfile(updatedData) {
    return this.request('/pandits/reapply', {
      method: 'PUT',
      body: updatedData,
    });
  }

  /**
   * Approve or reject a specific pandit document (admin only)
   * @param {String} documentId - Document ID to approve/reject
   * @param {String} status - New status ('approved' or 'rejected')
   * @returns {Promise} API response with updated document
   */
  async approveDocument(documentId, status) {
    return this.request(`/pandits/documents/${documentId}/approve`, {
      method: 'PUT',
      body: { status },
    });
  }

  // Booking Service endpoints
  async getAllServicesCategories(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/bookings/getAllServicesCategories${queryString}`);
  }

  async getServicesByCategory(categoryId, params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/bookings/getServicesByCategory/${categoryId}${queryString}`);
  }

  async getServiceById(serviceId) {
    return this.request(`/bookings/getServiceById/${serviceId}`);
  }

  async createBooking(bookingData) {
    return this.request('/bookings/createBooking', {
      method: 'POST',
      body: bookingData,
    });
  }

  async getAllBookingsByUser(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/bookings/getAllBookingsByUser${queryString}`);
  }

  async getBookingById(bookingId) {
    return this.request(`/bookings/getBookingById/${bookingId}`);
  }

  async getPanditBlockedSlots(userId) {
    return this.request(`/bookings/blocked-slots${userId ? `?userId=${userId}` : ''}`);
  }

  async getServicesByPandit(panditId, params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/bookings/getServicesByPandit/${panditId}${queryString}`);
  }

  async getMyPanditServices(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/bookings/pandit/my-services${queryString}`);
  }

  async createPanditService(serviceData) {
    return this.request('/bookings/pandit/services', {
      method: 'POST',
      body: serviceData,
    });
  }

  async updatePanditService(serviceId, updateData) {
    return this.request(`/bookings/pandit/services/${serviceId}`, {
      method: 'PUT',
      body: updateData,
    });
  }

  async deletePanditService(serviceId) {
    return this.request(`/bookings/pandit/services/${serviceId}`, {
      method: 'DELETE',
    });
  }

  // Admin booking management endpoints
  async getAllBookings(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/admin/bookings${queryString}`);
  }

  async updateBookingStatus(bookingId, status) {
    return this.request(`/admin/bookings/${bookingId}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  async getBookingStats() {
    return this.request('/admin/bookings/stats');
  }

  // E-Library endpoints
  async getAllELibraryBooks(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/e-library/books${queryString}`);
  }

  async getELibraryBookById(bookId) {
    return this.request(`/e-library/books/${bookId}`);
  }

  async createELibraryBook(bookData) {
    // Check if bookData is FormData (contains files)
    const isFormData = bookData instanceof FormData;

    if (isFormData) {
      // Use the upload endpoint for file uploads
      return this.request('/upload/book/complete', {
        method: 'POST',
        body: bookData, // FormData for file upload
      });
    } else {
      // Use the regular e-library endpoint for URL-based creation
      return this.request('/e-library/books', {
        method: 'POST',
        body: bookData,
      });
    }
  }

  async updateELibraryBook(bookId, bookData) {
    return this.request(`/e-library/books/${bookId}`, {
      method: 'PUT',
      body: bookData,
    });
  }

  async deleteELibraryBook(bookId) {
    return this.request(`/e-library/books/${bookId}`, {
      method: 'DELETE',
    });
  }

  async getELibraryStats() {
    return this.request('/e-library/stats');
  }

  async updateELibraryBookStatus(bookId, status) {
    return this.request(`/e-library/books/${bookId}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  // Chapter endpoints
  async getBookChapters(bookId, params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/e-library/books/${bookId}/chapters${queryString}`);
  }

  async getChapterById(chapterId) {
    return this.request(`/e-library/chapters/${chapterId}`);
  }

  async createChapter(bookId, chapterData) {
    return this.request(`/e-library/books/${bookId}/chapters`, {
      method: 'POST',
      body: chapterData,
    });
  }

  async updateChapter(chapterId, chapterData) {
    return this.request(`/e-library/chapters/${chapterId}`, {
      method: 'PUT',
      body: chapterData,
    });
  }

  async deleteChapter(chapterId) {
    return this.request(`/e-library/chapters/${chapterId}`, {
      method: 'DELETE',
    });
  }

  // Admin Service Management endpoints
  // Expected response format for getAllAdminServices:
  // {
  //   success: true,
  //   message: "Services fetched successfully",
  //   data: [
  //     {
  //       id: string,
  //       title: string,
  //       slug: string,
  //       categoryId: string,
  //       basePrice: number,
  //       durationMin: number,
  //       description?: string,
  //       active: boolean,
  //       createdAt: string,
  //       updatedAt: string,
  //       category: {
  //         id: string,
  //         name: string
  //       },
  //       panditExpertise: array,
  //       addons: array,
  //       regions: array
  //     }
  //   ],
  //   pagination: {
  //     page: number,
  //     limit: number,
  //     total: number,
  //     totalPages: number,
  //     hasNext: boolean,
  //     hasPrev: boolean
  //   },
  //   stats: {
  //     total: number,
  //     active: number,
  //     inactive: number,
  //     categories: number
  //   },
  //   timestamp: string
  // }
  async getAllAdminServices(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/services/admin/services${queryString}`);
  }

  async getAdminServiceById(serviceId) {
    return this.request(`/services/admin/services/${serviceId}`);
  }

  // Expected request format for createAdminService:
  // {
  //   title: string (required),
  //   categoryId: string (required),
  //   basePrice: number (required),
  //   durationMin: number (required),
  //   description?: string,
  //   active?: boolean (default: true),
  //   addons?: [
  //     {
  //       title: string,
  //       price: number,
  //       description?: string
  //     }
  //   ],
  //   regions?: [
  //     {
  //       state: string,
  //       language: string,
  //       priceModifier?: number
  //     }
  //   ]
  // }
  async createAdminService(serviceData) {
    return this.request('/services/admin/services', {
      method: 'POST',
      body: serviceData,
    });
  }

  async updateAdminService(serviceId, serviceData) {
    return this.request(`/services/admin/services/${serviceId}`, {
      method: 'PUT',
      body: serviceData,
    });
  }

  async deleteAdminService(serviceId) {
    return this.request(`/services/admin/services/${serviceId}`, {
      method: 'DELETE',
    });
  }

  async updateAdminServiceStatus(serviceId, active) {
    return this.request(`/services/admin/services/${serviceId}/status`, {
      method: 'PUT',
      body: { active },
    });
  }

  // Expected response format for getAdminServiceStats:
  // {
  //   success: true,
  //   message: "Service statistics fetched successfully",
  //   data: {
  //     totalServices: number,
  //     activeServices: number,
  //     inactiveServices: number,
  //     totalCategories: number,
  //     servicesByCategory: [
  //       {
  //         categoryId: string,
  //         categoryName: string,
  //         count: number
  //       }
  //     ]
  //   },
  //   timestamp: string
  // }
  async getAdminServiceStats() {
    return this.request('/services/admin/stats');
  }

  // Admin Category Management endpoints
  // Expected response format for getAllAdminCategories:
  // {
  //   success: true,
  //   message: "Categories fetched successfully",
  //   data: [
  //     {
  //       id: string,
  //       name: string,
  //       parentId?: string,
  //       iconUrl?: string,
  //       isActive: boolean,
  //       createdAt: string,
  //       updatedAt: string,
  //       parent?: {
  //         id: string,
  //         name: string
  //       },
  //       children: [
  //         {
  //           id: string,
  //           name: string,
  //           iconUrl?: string
  //         }
  //       ],
  //       serviceCount: number
  //     }
  //   ],
  //   timestamp: string
  // }
  async getAllAdminCategories(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/services/admin/categories${queryString}`);
  }

  async getAdminCategoryById(categoryId) {
    return this.request(`/services/admin/categories/${categoryId}`);
  }

  // Expected request format for createAdminCategory:
  // {
  //   name: string (required),
  //   parentId?: string,
  //   iconUrl?: string,
  //   isActive?: boolean (default: true)
  // }
  async createAdminCategory(categoryData) {
    return this.request('/services/admin/categories', {
      method: 'POST',
      body: categoryData,
    });
  }

  async updateAdminCategory(categoryId, categoryData) {
    return this.request(`/services/admin/categories/${categoryId}`, {
      method: 'PUT',
      body: categoryData,
    });
  }

  async deleteAdminCategory(categoryId) {
    return this.request(`/services/admin/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // Expected response format for getAdminCategoryStats:
  // {
  //   success: true,
  //   message: "Category statistics fetched successfully",
  //   data: {
  //     totalCategories: number,
  //     activeCategories: number,
  //     inactiveCategories: number,
  //     rootCategories: number,
  //     topCategories: [
  //       {
  //         id: string,
  //         name: string,
  //         serviceCount: number
  //       }
  //     ]
  //   },
  //   timestamp: string
  // }
  async getAdminCategoryStats() {
    return this.request('/services/admin/categories/stats');
  }

  // Dashboard endpoints
  /**
   * Get dashboard statistics
   * @returns {Promise} Dashboard stats including totals, trends, and recent data
   */
  async getDashboardStats() {
    return this.request('/dashBoard/stats');
  }

  // Financial / Commission Endpoints
  async getGlobalCommission() {
    return this.request('/admin/commission');
  }

  async getPanditCommission(panditId) {
    return this.request(`/admin/commission/pandit/${panditId}`);
  }

  async updatePanditCommission(panditId, percentage, type) {
    return this.request(`/admin/commission/pandit/${panditId}`, {
      method: 'PUT',
      body: { percentage, type }
    });
  }

  /**
   * Get booking trends for specified date range
   * @param {number} range - Number of days to get trends for (default: 7)
   * @returns {Promise} Booking trend data
   */
  async getBookingTrends(range = 7) {
    return this.request(`/dashBoard/booking-trend?range=${range}`);
  }

  /**
   * Get user registration trends for specified date range
   * @param {number} range - Number of days to get trends for (default: 14)
   * @returns {Promise} User registration trend data
   */
  async getUserTrends(range = 14) {
    return this.request(`/dashBoard/user-trend?range=${range}`);
  }

  // ==================== CONTACT MANAGEMENT ====================

  /**
   * Get all contact options for admin (including inactive)
   * @returns {Promise} List of all contact options
   */
  async getAllContactOptions() {
    return this.request('/contact/admin/all');
  }

  /**
   * Get all active contact options (for public)
   * @returns {Promise} List of active contact options
   */
  async getActiveContactOptions() {
    return this.request('/contact/active');
  }

  /**
   * Create a new contact option
   * @param {Object} contactData - Contact option data
   * @returns {Promise} Created contact option
   */
  async createContactOption(contactData) {
    return this.request('/contact/admin', {
      method: 'POST',
      body: contactData,
    });
  }

  /**
   * Update an existing contact option
   * @param {string} id - Contact option ID
   * @param {Object} contactData - Updated contact option data
   * @returns {Promise} Updated contact option
   */
  async updateContactOption(id, contactData) {
    return this.request(`/contact/admin/${id}`, {
      method: 'PUT',
      body: contactData,
    });
  }

  /**
   * Delete a contact option
   * @param {string} id - Contact option ID
   * @returns {Promise} Deletion result
   */
  async deleteContactOption(id) {
    return this.request(`/contact/admin/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Toggle contact option active status
   * @param {string} id - Contact option ID
   * @returns {Promise} Updated contact option
   */
  async toggleContactOption(id) {
    return this.request(`/contact/admin/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  // ==================== FINANCIAL MANAGEMENT ====================

  async getGlobalCommission() {
    return this.request('/financial/admin/commission');
  }

  async updateGlobalCommission(percentage) {
    return this.request('/financial/admin/commission', {
      method: 'POST',
      body: { percentage },
    });
  }

  async getPanditCommission(panditId) {
    return this.request(`/financial/admin/commission/pandit/${panditId}`);
  }

  async updatePanditCommission(panditId, percentage) {
    return this.request(`/financial/admin/commission/pandit/${panditId}`, {
      method: 'PUT',
      body: { percentage },
    });
  }

  async getAdminFinancialStats() {
    return this.request('/financial/admin/stats');
  }

  async getPayoutRequests(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/financial/admin/payouts${queryString}`);
  }

  async processPayoutRequest(payoutId, action, notes, transactionRef) {
    return this.request('/financial/admin/payouts/process', {
      method: 'POST',
      body: { payoutId, action, notes, transactionRef }
    });
  }

  // ==================== REFUND MANAGEMENT ====================

  async getAllRefunds(params) {
    // Check if params is already an object, if so, convert to query string
    let queryString = '';
    if (params) {
      if (typeof params === 'object') {
        queryString = '?' + new URLSearchParams(params).toString();
      } else {
        queryString = `?${params.toString()}`;
      }
    }
    return this.request(`/admin/refunds${queryString}`);
  }

  async approveRefund(refundId) {
    return this.request(`/admin/refunds/${refundId}/approve`, {
      method: 'POST'
    });
  }

  // ==================== LIVE STREAM ====================
  async getLiveEvents(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/livestreams${queryString}`);
  }

  async updateLiveEventStatus(eventId, statusData) {
    return this.request(`/livestreams/${eventId}/status`, {
      method: 'PUT',
      body: statusData
    });
  }

  async getLiveEventAnalytics(eventId) {
    return this.request(`/livestreams/${eventId}/analytics`);
  }

  async getLiveStreamStats() {
    return this.request('/livestreams/stats');
  }

  // ==================== FESTIVAL MANAGEMENT ====================

  /**
   * Get all festivals with optional filtering
   * @param {Object} params - Query parameters (page, limit, category, isActive)
   * @returns {Promise} List of festivals with pagination
   */
  async getAllFestivals(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/festivals${queryString}`);
  }

  /**
   * Get upcoming festivals
   * @param {Object} params - Query parameters (limit, days)
   * @returns {Promise} List of upcoming festivals
   */
  async getUpcomingFestivals(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/festivals/upcoming${queryString}`);
  }

  /**
   * Get festivals by category
   * @param {string} category - Festival category
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} List of festivals in category
   */
  async getFestivalsByCategory(category, params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/festivals/category/${category}${queryString}`);
  }

  /**
   * Search festivals
   * @param {string} query - Search query
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} Search results
   */
  async searchFestivals(query, params) {
    const searchParams = { query, ...params };
    const queryString = `?${new URLSearchParams(searchParams).toString()}`;
    return this.request(`/festivals/search${queryString}`);
  }

  /**
   * Get single festival by ID
   * @param {string} festivalId - Festival ID
   * @returns {Promise} Festival details
   */
  async getFestivalById(festivalId) {
    return this.request(`/festivals/${festivalId}`);
  }

  /**
   * Create a new festival (Admin only)
   * @param {Object} festivalData - Festival data
   * @returns {Promise} Created festival
   */
  async createFestival(festivalData) {
    return this.request('/festivals', {
      method: 'POST',
      body: festivalData,
    });
  }

  /**
   * Update an existing festival (Admin only)
   * @param {string} festivalId - Festival ID
   * @param {Object} festivalData - Updated festival data
   * @returns {Promise} Updated festival
   */
  async updateFestival(festivalId, festivalData) {
    return this.request(`/festivals/${festivalId}`, {
      method: 'PUT',
      body: festivalData,
    });
  }

  /**
   * Delete a festival (Admin only)
   * @param {string} festivalId - Festival ID
   * @returns {Promise} Deletion result
   */
  async deleteFestival(festivalId) {
    return this.request(`/festivals/${festivalId}`, {
      method: 'DELETE',
    });
  }

  async updateCommission(data) {
    return this.request('/admin/commission', {
      method: 'PUT',
      body: data,
    });
  }

  // ==========================================
  // Puja Management Endpoints
  // ==========================================

  // Categories
  async getAllPujaCategories(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/puja/categories${queryString}`);
  }

  async getPujaCategoryById(id) {
    return this.request(`/puja/categories/${id}`);
  }

  async createPujaCategory(data) {
    return this.request('/puja/categories', {
      method: 'POST',
      body: data,
    });
  }

  async updatePujaCategory(id, data) {
    return this.request(`/puja/categories/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async deletePujaCategory(id) {
    return this.request(`/puja/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Pujas (Services)
  async getAllPujas(params) {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request(`/puja/services${queryString}`);
  }

  async getPujaById(id) {
    return this.request(`/puja/services/${id}`);
  }

  async createPuja(data) {
    return this.request('/puja/services', {
      method: 'POST',
      body: data,
    });
  }

  async updatePuja(id, data) {
    return this.request(`/puja/services/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async deletePuja(id) {
    return this.request(`/puja/services/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== MUHURAT MANAGEMENT ====================

  /**
   * Get all muhurats with optional filtering
   */
  async getAllMuhurats(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/muhurats${queryString}`);
  }

  /**
   * Get upcoming muhurats
   */
  async getUpcomingMuhurats() {
    return this.request('/muhurats/upcoming');
  }

  /**
   * Get single muhurat by ID
   */
  async getMuhuratById(id) {
    return this.request(`/muhurats/${id}`);
  }

  /**
   * Create a new muhurat (Admin only)
   */
  async createMuhurat(muhuratData) {
    return this.request('/muhurats', {
      method: 'POST',
      body: muhuratData,
    });
  }

  /**
   * Update an existing muhurat (Admin only)
   */
  async updateMuhurat(id, muhuratData) {
    return this.request(`/muhurats/${id}`, {
      method: 'PUT',
      body: muhuratData,
    });
  }

  /**
   * Delete a muhurat (Admin only)
   */
  async deleteMuhurat(id) {
    return this.request(`/muhurats/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get muhurat dashboard stats
   */
  async getMuhuratStats() {
    return this.request('/muhurats/admin/stats');
  }

  // ==========================================
  // Testimonial Endpoints
  // ==========================================

  /**
   * Get all testimonials (Admin)
   * @param {Object} params - Query parameters (status, page, limit)
   * @returns {Promise} List of testimonials
   */
  async getAllTestimonials(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/testimonials/admin${queryString}`);
  }

  /**
   * Update testimonial status (Admin)
   * @param {string} id - Testimonial ID
   * @param {Object} data - Update data (e.g., { isApproved: true })
   */
  async updateTestimonial(id, data) {
    return this.request(`/testimonials/admin/${id}`, {
      method: 'PATCH',
      body: data,
    });
  }

  // ==========================================
  // Broadcast Notification Endpoints
  // ==========================================

  /**
   * Send broadcast notification
   * @param {Object} data - Notification data
   * @param {string} data.title - Notification title
   * @param {string} data.body - Notification message
   * @param {string} data.targetAudience - Target audience: 'all', 'pandits', or 'users'
   * @param {Object} [data.additionalData] - Additional data to send with notification
   */
  async sendBroadcastNotification(data) {
    return this.request('/admin/broadcast', {
      method: 'POST',
      body: data,
    });
  }

  /**
   * Get broadcast notification statistics
   * @returns {Promise} Statistics about users with active FCM tokens
   */
  async getBroadcastStats() {
    return this.request('/admin/broadcast/stats');
  }

  // ==========================================
  // Tirth Sthal (Pilgrimage Places) Endpoints
  // ==========================================

  /**
   * Get all Tirth Sthals
   * @param {Object} params - Query parameters
   * @returns {Promise} List of Tirth Sthals with pagination
   */
  async getAllTirthSthals(params) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/tirth-sthals${queryString}`);
  }

  /**
   * Get featured Tirth Sthals
   * @returns {Promise} List of featured Tirth Sthals
   */
  async getFeaturedTirthSthals() {
    return this.request('/tirth-sthals/featured');
  }

  /**
   * Get Tirth Sthals by category
   * @param {string} category - Category
   * @returns {Promise} List of Tirth Sthals in category
   */
  async getTirthSthalsByCategory(category) {
    return this.request(`/tirth-sthals/category/${category}`);
  }

  /**
   * Get nearby Tirth Sthals
   * @param {number} latitude - Latitude
   * @param {number} longitude - Longitude
   * @param {number} radius - Radius in kilometers
   * @returns {Promise} List of nearby Tirth Sthals
   */
  async getNearbyTirthSthals(latitude, longitude, radius = 100) {
    const params = { latitude, longitude, radius };
    const queryString = `?${new URLSearchParams(params).toString()}`;
    return this.request(`/tirth-sthals/nearby${queryString}`);
  }

  /**
   * Get single Tirth Sthal by ID
   * @param {string} id - Tirth Sthal ID
   * @returns {Promise} Tirth Sthal details
   */
  async getTirthSthalById(id) {
    return this.request(`/tirth-sthals/${id}`);
  }

  /**
   * Create a new Tirth Sthal (Admin only)
   * @param {Object} tirthSthalData - Tirth Sthal data
   * @returns {Promise} Created Tirth Sthal
   */
  async createTirthSthal(tirthSthalData) {
    return this.request('/tirth-sthals', {
      method: 'POST',
      body: tirthSthalData,
    });
  }

  /**
   * Update an existing Tirth Sthal (Admin only)
   * @param {string} id - Tirth Sthal ID
   * @param {Object} tirthSthalData - Updated Tirth Sthal data
   * @returns {Promise} Updated Tirth Sthal
   */
  async updateTirthSthal(id, tirthSthalData) {
    return this.request(`/tirth-sthals/${id}`, {
      method: 'PUT',
      body: tirthSthalData,
    });
  }

  /**
   * Delete a Tirth Sthal (Admin only)
   * @param {string} id - Tirth Sthal ID
   * @returns {Promise} Deletion result
   */
  async deleteTirthSthal(id) {
    return this.request(`/tirth-sthals/${id}`, {
      method: 'DELETE',
    });
  }

  // ─── Banner Management ─────────────────────────────────────────────────────

  async getAllBanners(params = {}) {
    const queryString = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/banners${queryString}`);
  }

  async createBanner(bannerData) {
    return this.request('/banners', { method: 'POST', body: bannerData });
  }

  async updateBanner(id, bannerData) {
    return this.request(`/banners/${id}`, { method: 'PUT', body: bannerData });
  }

  async deleteBanner(id) {
    return this.request(`/banners/${id}`, { method: 'DELETE' });
  }

  // ─── Vicharniye Mahurat ─────────────────────────────────────────────────────

  async getAllVicharniyeMahurat(params = {}) {
    const queryString = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/vicharniye-mahurat${queryString}`);
  }

  async getVicharniyeMahuratById(id) {
    return this.request(`/vicharniye-mahurat/${id}`);
  }

  async createVicharniyeMahurat(data) {
    return this.request('/vicharniye-mahurat', { method: 'POST', body: data });
  }

  async updateVicharniyeMahurat(id, data) {
    return this.request(`/vicharniye-mahurat/${id}`, { method: 'PUT', body: data });
  }

  async deleteVicharniyeMahurat(id) {
    return this.request(`/vicharniye-mahurat/${id}`, { method: 'DELETE' });
  }

  // ─── Vedon ke Vichar ────────────────────────────────────────────────────────

  async getAllVedonKeVichar(params = {}) {
    const queryString = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/vedon-ke-vichar${queryString}`);
  }

  async getVedonKeVicharById(id) {
    return this.request(`/vedon-ke-vichar/${id}`);
  }

  async createVedonKeVichar(data) {
    return this.request('/vedon-ke-vichar', { method: 'POST', body: data });
  }

  async updateVedonKeVichar(id, data) {
    return this.request(`/vedon-ke-vichar/${id}`, { method: 'PUT', body: data });
  }

  async deleteVedonKeVichar(id) {
    return this.request(`/vedon-ke-vichar/${id}`, { method: 'DELETE' });
  }

}

export default new ApiService();
